// useSync.ts — wires the sync service (services/sync.ts) to live app state.
//
// Mounted once near the app root. Fail-soft: any network problem is swallowed and
// retried later; sync never blocks or breaks the UI. Three behaviours:
//   • On sign-in with a real token: pull remote progress, merge, apply, and flush
//     any queued (offline) research posts — once per token.
//   • On journey-state change: debounce-push the operational progress blob.
//   • Local-trial / signed-out sessions do nothing (no server-verifiable identity).
import React from 'react';
import { App as CapApp } from '@capacitor/app';
import { useAuth } from './useAuth';
import { useProgress } from './useProgress';
import { canSync, toSyncBlob, mergeBlobs, pullProgress, pullReachedServer, pushProgress, flushQueue, pushResearchProgress, clearSyncState, type SyncBlob } from '@/services/sync';
import { flushConsentQueue, clearConsentQueue } from '@/services/consent';
import { storage } from '@/services/storage';
import { PROGRESS_SEED } from '@/data/progress-seed';

const VERSION_KEY = 'renurture.sync.version.v1';
// The last user whose data is on this device, so we can tell a returning user
// (keep their local data — lossless sign-out/in) from a NEW person on a shared
// device (wipe the previous user's local data before pulling theirs).
const LAST_USER_KEY = 'renurture.sync.lastUser.v1';
// Per-user local backups taken the moment BEFORE a switch-user wipe. This is the
// safety net for the data-loss bug: if a user's progress never reached the server
// (e.g. the backend was unreachable and every push aborted), switching accounts
// would otherwise destroy the only copy. We snapshot the outgoing user's blob here
// and restore it if they sign back in and the server has nothing newer.
const BACKUP_KEY = 'renurture.sync.backup.v1';
const MAX_BACKUPS = 3; // bound growth on a shared device — keep the few most recent
const EMPTY_BLOB = toSyncBlob(PROGRESS_SEED);
const PUSH_DEBOUNCE_MS = 2500;
const PUSH_RETRY_MS = 20000; // re-attempt a failed push so a transient drop isn't lost
const PUSH_MAX_RETRIES = 4;

type BackupMap = Record<string, { blob: SyncBlob; savedAt: number }>;

function saveBackup(userId: string, blob: SyncBlob, now: number): void {
  if (!userId || !blob) return;
  const map = storage.getJSON<BackupMap>(BACKUP_KEY, {}) || {};
  map[userId] = { blob, savedAt: now };
  // Keep only the most-recently-saved few users so the store can't grow unbounded.
  const pruned: BackupMap = {};
  for (const id of Object.keys(map).sort((a, b) => map[b].savedAt - map[a].savedAt).slice(0, MAX_BACKUPS)) {
    pruned[id] = map[id];
  }
  storage.setJSON(BACKUP_KEY, pruned);
}

function readBackup(userId: string): SyncBlob | null {
  if (!userId) return null;
  const map = storage.getJSON<BackupMap>(BACKUP_KEY, {}) || {};
  return map[userId]?.blob ?? null;
}

// "Local progress is ahead of the server." Set the instant progress changes and
// cleared only when the server ACKs a push. It persists across app death, so if a
// push never landed (e.g. the backend was briefly unreachable) the next app open
// re-sends it — the server self-heals to current without relying on the local copy
// surviving. This is what makes progress continuously stored server-side.
const DIRTY_KEY = 'renurture.sync.dirty.v1';
const markDirty = (): void => storage.set(DIRTY_KEY, '1');
const clearDirty = (): void => storage.remove(DIRTY_KEY);
const isDirty = (): boolean => storage.get(DIRTY_KEY) === '1';

function nextVersion(): number {
  const v = (storage.getJSON<number>(VERSION_KEY, 0) || 0) + 1;
  storage.setJSON(VERSION_KEY, v);
  return v;
}

export function useSync(): void {
  const { session } = useAuth();
  const progress = useProgress();
  const token = canSync(session) ? session.accessToken : null;
  const userId = session?.user.userId ?? null;

  // Stable refs so the debounced push always sees the latest state without
  // re-subscribing the effect on every keystroke-level change.
  const blobRef = React.useRef<SyncBlob | null>(null);
  blobRef.current = toSyncBlob(progress.state);
  const applyRef = React.useRef(progress.applySyncBlob);
  applyRef.current = progress.applySyncBlob;
  const resetRef = React.useRef(progress.reset);
  resetRef.current = progress.reset;
  // Latest state for the research progress push (read at call time, not stale).
  const stateRef = React.useRef(progress.state);
  stateRef.current = progress.state;

  const pulledForToken = React.useRef<string | null>(null);
  const pushTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const pushFails = React.useRef(0);
  const pulling = React.useRef(false);

  // Latest token without re-subscribing lifecycle listeners on every refresh.
  const tokenRef = React.useRef<string | null | undefined>(null);
  tokenRef.current = token;

  // Push the current blob immediately, bypassing the debounce. Used for the
  // flush-on-background path (so a change is saved at the last possible moment, not
  // stranded behind a debounce the user never waits out) and the mount self-heal.
  // Clears the dirty flag only on a confirmed server ACK.
  const flushNow = React.useCallback(() => {
    const t = tokenRef.current;
    if (!t || pulledForToken.current !== t || !blobRef.current) return;
    void pushProgress(t, blobRef.current, nextVersion()).then((ok) => { if (ok) clearDirty(); });
  }, []);

  // Silent foreground re-pull: when the app returns to focus, top this device up from
  // the server so progress + saved items made on another device appear without a
  // sign-out/in. GENUINELY BACKGROUND — no spinner, no blocking, failures swallowed.
  // The merge is a forward-only union, so it can only ADD; it can never undo local
  // progress or interrupt what the user is doing. Throttled so it never hammers the
  // backend on rapid focus changes. Runs only AFTER the initial pull has landed.
  const lastPullAt = React.useRef(0);
  // Foreground / reconnect pull. Normally a silent top-up AFTER the initial pull, but it
  // ALSO recovers when the initial sign-in pull FAILED (pulledForToken still unset):
  // reaching the server here marks the token pulled and unblocks pushing, so a transient
  // offline/401 at sign-in self-heals instead of stranding the session unsynced. The
  // union merge means this can only ever ADD; it never undoes local progress.
  const foregroundPull = React.useCallback(() => {
    const t = tokenRef.current;
    if (!t || pulling.current) return;
    if (Date.now() - lastPullAt.current < 45000) return; // at most ~once / 45s
    lastPullAt.current = Date.now();
    pulling.current = true;
    void (async () => {
      try {
        const remote = await pullProgress(t);
        if (pullReachedServer(remote) && remote && blobRef.current) {  // reached the server
          if (remote.data) {
            const merged = mergeBlobs(blobRef.current, remote.data);
            applyRef.current(merged);                    // forward-only union — only adds
            void pushProgress(t, merged, nextVersion()); // converge both sides
          }
          if (pulledForToken.current !== t) {            // recover a failed initial pull
            pulledForToken.current = t;
            if (isDirty()) flushNow();
          }
        }
      } catch { /* silent — never disturb the user */ }
      finally { pulling.current = false; }
    })();
  }, [flushNow]);

  // ── pull + merge + flush, once per real token ──
  React.useEffect(() => {
    if (!token || pulledForToken.current === token || pulling.current) return;
    pulling.current = true;
    let cancelled = false;
    (async () => {
      // Shared-device safety vs. lossless return. If a DIFFERENT person is signing
      // in than the one whose data is on this device, wipe the previous user's
      // local journey and queued sync BEFORE pulling — so they never inherit it.
      // The SAME user keeps everything local, so a sign-out / sign-in round-trip on
      // their own phone loses nothing — including the "moments of practice" counts.
      // We then pull from the server and union-merge, topping the device up.
      const lastUser = storage.get(LAST_USER_KEY);
      const switchedUser = !!userId && !!lastUser && lastUser !== userId;
      if (switchedUser) {
        // Back up the OUTGOING user's local data BEFORE wiping it. At this point the
        // in-memory blob is still theirs (React hasn't re-rendered for the new user),
        // so this captures the only copy of any progress that never reached the
        // server — the safety net against the account-switch data-loss bug.
        if (lastUser && blobRef.current) saveBackup(lastUser, blobRef.current, Date.now());
        resetRef.current();
        clearSyncState();
        clearConsentQueue(); // drop the previous user's unsynced consent decisions
      }
      if (userId) storage.set(LAST_USER_KEY, userId);
      // After a switch-user reset the in-memory blob is stale (still the previous
      // user's, until React re-renders), so merge from an explicit empty base. If we
      // hold a prior local backup for the INCOMING user, fold it in first — so a
      // returning user recovers anything the server didn't persist. mergeBlobs is a
      // safe union, so the server pull below can only add to this, never undo it.
      let base = switchedUser ? EMPTY_BLOB : blobRef.current!;
      if (switchedUser) {
        const backup = readBackup(userId!);
        if (backup) base = mergeBlobs(base, backup);
      }

      const remote = await pullProgress(token);
      // remote === null means the pull FAILED (offline / 401 / 5xx) — the server state
      // is UNKNOWN. We must NOT mark this token "pulled" or run the self-heal flush in
      // that case, or an empty/stale local could overwrite a healthy server (the
      // progress-reset data-loss bug). A failed pull just leaves pulledForToken unset so
      // pushes stay blocked; foregroundPull (app focus / reconnect) retries and recovers.
      // A SUCCESSFUL pull of an empty server is { data: null } and is safe to proceed on
      // (a genuinely new user has nothing to clobber).
      const pullReached = !cancelled && pullReachedServer(remote);
      if (pullReached && remote && remote.data) {
        const merged = mergeBlobs(base, remote.data);
        applyRef.current(merged);
        // Push the merged result straight back so both sides converge.
        void pushProgress(token, merged, nextVersion());
      }
      await flushQueue(token);
      // Send any consent decisions captured while offline (e.g. an offline withdrawal).
      void flushConsentQueue();
      // Engagement snapshot for corporate/cohort reporting — consent-gated, once/day.
      if (!cancelled && stateRef.current.researchConsent === 'granted') {
        void pushResearchProgress(token, stateRef.current);
      }
      if (pullReached) {
        pulledForToken.current = token;
        // Self-heal: if a previous session left progress unsynced (server was down),
        // re-send it now that we are back online. Safe ONLY because we have just reached
        // the server and merged its current copy into local.
        if (isDirty()) flushNow();
      }
      pulling.current = false;
    })();
    return () => {
      cancelled = true;
      pulling.current = false;
    };
  }, [token, userId]);

  // ── debounced push on journey change ──
  const blobKey = React.useMemo(() => JSON.stringify(blobRef.current), [progress.state]);
  React.useEffect(() => {
    if (!token || pulledForToken.current !== token) return; // wait until the initial pull/merge has landed
    markDirty(); // local is now ahead of the server; survives app death until ACKed
    if (pushTimer.current) clearTimeout(pushTimer.current);
    pushFails.current = 0;
    // Push (debounced); on failure re-attempt a few times so a transient network
    // drop doesn't strand the user's latest progress on this device only. The blob
    // is read fresh each attempt, and the union merge means a late push never loses
    // data — this just shortens the window before other devices see the change.
    const attempt = () => {
      void pushProgress(token, blobRef.current!, nextVersion()).then((okPush) => {
        if (okPush) { clearDirty(); pushFails.current = 0; return; } // server has it now
        // Exhausted in-session retries: stay dirty so the next foreground/app-open
        // re-sends it. We never silently give up on the user's progress.
        if (pushFails.current >= PUSH_MAX_RETRIES) { pushFails.current = 0; return; }
        pushFails.current += 1;
        pushTimer.current = setTimeout(attempt, PUSH_RETRY_MS);
      });
    };
    pushTimer.current = setTimeout(attempt, PUSH_DEBOUNCE_MS);
    return () => {
      if (pushTimer.current) clearTimeout(pushTimer.current);
    };
  }, [token, blobKey]);

  // ── flush on background / tab-hide ──
  // The debounce means the last change before the user backgrounds the app could
  // sit unsent. Push immediately when the app goes to the background (native
  // appStateChange — most reliable on mobile), the tab is hidden, or the page is
  // unloaded. flushNow is a no-op until the initial pull has landed.
  React.useEffect(() => {
    // Going to background → flush the last change up. Coming back to foreground →
    // silently pull anything new down (so another device's saves appear).
    const onVis = () => { if (document.hidden) flushNow(); else foregroundPull(); };
    const onHide = () => flushNow();
    // Reconnect → retry the pull. This is what recovers a sign-in pull that failed while
    // offline (pulledForToken stays unset until a pull reaches the server), without
    // waiting for the next app-focus event.
    const onOnline = () => foregroundPull();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', onHide);
    window.addEventListener('online', onOnline);
    const sub = CapApp.addListener('appStateChange', ({ isActive }: { isActive: boolean }) => {
      if (!isActive) flushNow(); else foregroundPull();
    });
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', onHide);
      window.removeEventListener('online', onOnline);
      void sub.then((s) => s.remove());
    };
  }, [flushNow, foregroundPull]);
}
